import { defineStore } from 'pinia';
import genericAPI from "@/api/api";
import type { ZUser } from "@schemas/user.schemas";
import type {Id} from "@typesDef/general.types";
import { setIdsUnique } from "./helpers/storeHelper";

export const useUserStore = defineStore('user', {
    state: () => ({
        usersById: {} as Record<Id, ZUser>,
        myUserId: null as Id | null,
        currentUserId: null as Id | null,
    }),
    getters: {
        myUser(state): ZUser | null {
            return state.myUserId ? state.usersById[state.myUserId] ?? null : null;
        },
        currentUser(state): ZUser | null {
            return state.currentUserId ? state.usersById[state.currentUserId] ?? null : null;
        }
    },
    actions: {
        clearMyData(): void {
            this.usersById = {};
            this.myUserId = null;
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZUser[]) {
            for (const e of docs) this.usersById[e._id] = e
        },

        /**
         * Fetch the current user from the backend
         * If force is true, the user will be fetched from the backend even if it is already in the store
         * @param force
         */
        async fetchMyUser(force: boolean = false): Promise<ZUser> {
            log.debug(`fetchMyUser force=${force}`);
            if(this.myUserId && !force) {
                const cached = this.usersById[this.myUserId];
                if(cached) {
                    return cached;
                }
            }
            // If we have a myUserId but it's not in the cache, we should try to fetch it directly by ID before fetching the current user endpoint
            const doc = await genericAPI.getDocument<ZUser>('users', 'current');
            this.usersById[doc._id] = doc;
            this.myUserId = doc._id;
            return doc;
        },

        async fetchUser(userId: Id): Promise<ZUser> {
            log.debug(`fetchUser(${userId})`);
            const cached = this.usersById[userId];
            if(cached) {
                this.currentUserId = userId; // set the currentUserId to the fetched user
                return cached;
            }
            const doc = await genericAPI.getDocument<ZUser>('users', userId);
            if(!doc?._id) {
                throw new Error(`User with id ${userId} not found`);
            }
            this.usersById[doc._id] = doc;
            this.currentUserId = doc._id;
            return doc;
        },
        async createUser(user: ZUser): Promise<ZUser> {
            const created = await genericAPI.postDocument<ZUser>('users', user);
            if(!created?._id) {
                throw new Error(`Failed to create user`);
            }
            this.usersById[created._id] = created;
            return created;
        },
        async updateUser(user: ZUser): Promise<ZUser> {
            const updated = await genericAPI.putDocument<ZUser>('users', user);
            if(!updated?._id) {
                throw new Error(`Failed to create user`);
            }
            this.usersById[updated._id] = updated;
            return updated
        },
        async deleteUser(userId: Id): Promise<void> {
            await genericAPI.deleteDocument<ZUser>('users', userId);
            delete this.usersById[userId];
            // if the deleted user is the current user, clear my data
            if (this.myUserId === userId) {
                this.myUserId = null;
            }
            // if the deleted user is the public user, clear public user
            if (this.currentUserId === userId) {
                this.currentUserId = null;
            }
        }
    }
});
